import { TournamentLobbyView, TournamentPlayerVM } from './TournamentLobbyView';
import type { BracketData, MatchLiveStatus, NameResolver } from './BracketView';
import { v5 as uuidv5 } from 'uuid';
import { sb } from '../data';
import { showTournamentAwardsShow } from './AwardsShow';
import { BYE_USER_ID } from './RoundRobinScheduler';
import type { RoundRobinMatch } from './RoundRobinScheduler';
import { showBoxscoreModal } from '../boxscore';
import { TIMING } from '../constants/timing';

export class TournamentLobbyController {
  private view: TournamentLobbyView;
  private channel: any = null;
  private tournamentId!: string;
  private currentUserId!: string;
  private isCreator = false;
  private name = 'Tournament';
  private creatorId: string | null = null;
  private joinCode: string | undefined;
  private status: string = 'registration';
  private bracketData: BracketData | null = null;
  private tournamentType: 'bracket' | 'round_robin' = 'bracket';
  private roundRobinData: any = null;
  private roundRobinViewMode: 'schedule' | 'standings' = 'schedule';
  private pollInterval: any = null;
  private playerNameCache: Map<string, string> = new Map();
  // Feature flag: show awards on leaderboard even before tournament is completed
  private showAwardsEarly: boolean = false;

  // Global user cache to avoid duplicate getUserById calls
  private userCache: Map<string, { user_name?: string; profile_image_url?: string }> = new Map();
  // PPR cache to avoid duplicate calculateUserPPRForTournament calls within same render
  private pprCache: Map<string, number> = new Map();
  private pprCacheTimestamp: number = 0;
  // Tournament players cache to avoid duplicate listTournamentPlayers calls
  private tournamentPlayersCache: Array<{ user_id: string; seed: number | null; stats?: any }> | null = null;
  private tournamentPlayersCacheTs: number = 0;
  private lobbyMode: 'registration' | 'bracket' | 'leaderboard' | 'schedule' | 'standings' = 'registration';

  constructor(private deps: {
    getTournamentById: (id: string) => Promise<any>;
    listTournamentPlayers: (id: string) => Promise<Array<{ user_id: string; seed: number | null }>>;
    getUserById: (id: string) => Promise<any>;
    leaveTournament: (tournamentId: string, userId: string) => Promise<void>;
    startTournament: (id: string) => Promise<any>;
    startRoundRobinTournament?: (id: string) => Promise<any>;
    applyBracketAutoAdvances?: (tournamentId: string) => Promise<any>;
    getGameById: (gameId: string) => Promise<any | null>;
    resumeGame: (gameId: string) => Promise<void>;
    findLatestGameBetweenUsers: (userAId: string, userBId: string) => Promise<any | null>;
    findTournamentGame: (userAId: string, userBId: string, tournamentId: string, round: number, matchIndex: number) => Promise<any | null>;
    recordTournamentMatchResult: (args: { tournamentId: string; round: number; matchIndex: number; aScore: number; bScore: number; winnerUserId: string }) => Promise<any>;
    calculateUserPPRForTournament: (tournamentId: string, userId: string) => Promise<number>;
    calculateStandingsFromRollHistories: (tournamentId: string, matches: Array<{ playerA: { userId: string }; playerB: { userId: string }; result?: any }>) => Promise<any>;
    upsertTournamentPlayerStats: (tournamentId: string, userId: string, delta: any) => Promise<any>;
    showStartMatchModal: (args: { meId: string; opponentId: string; title?: string; matchId?: string; tournamentId: string; round: number; matchIndex: number; startingPlayerId?: string }) => Promise<void>;
    getTournamentMatchGame?: (tournamentId: string, round: number, matchIndex: number) => Promise<{ gameId: string; matchId: string; player1: any; player2: any } | null>;
    initializeAsObserver?: (matchId: string, player1: any, player2: any, opts?: { tournamentId?: string; round?: number; matchIndex?: number }) => Promise<void>;
    saveTournamentAwardWinners?: (tournamentId: string, awards: any) => Promise<void>;
    showUserProfile?: (userId: string, userName: string) => void;
  }) {
    this.view = new TournamentLobbyView({
      onStart: () => this.start(),
      onLeave: () => this.leave(),
      onBack: () => this.destroy(),
      onCancel: () => {/* no-op for now */},
      onMatchClick: (info) => this.handleMatchClick(info),
      onPlayerProfile: (userId, userName) => this.showUserProfile(userId, userName),
      onShowBracket: () => this.showPrimaryView(),
      onShowLeaderboard: () => this.showSecondaryView(),
      onRefresh: () => this.manualRefresh(),
    });
  }

  public async open(tournamentId: string, currentUserId: string) {
    this.tournamentId = tournamentId;
    this.currentUserId = currentUserId;
    // Defensive: ensure this is the only tourney view and hide underlying UIs
    // Remove any previously orphaned tourney views
    Array.from(document.querySelectorAll('#tourney-lobby')).forEach((el) => {
      try { el.parentElement?.removeChild(el); } catch {}
    });
    // Hide the main lobby beneath so body background shows through cleanly
    const mainLobby = document.getElementById('skeech-lobby');
    if (mainLobby) mainLobby.style.display = 'none';
    // Also hide the board root so we never stack with the bracket view
    const boardRoot = document.getElementById('skeech-root');
    if (boardRoot) boardRoot.style.display = 'none';

    const t = await this.deps.getTournamentById(tournamentId);
    this.name = t?.name || 'Tournament';
    this.creatorId = t?.creator_id || null;
    this.isCreator = this.creatorId === currentUserId;
    this.joinCode = t?.join_code;
    this.status = t?.status || 'registration';
    this.tournamentType = t?.tournament_type || 'bracket';
    this.roundRobinData = t?.round_robin_data || null;

    // Backfill: ensure host is enrolled in tournament_players (handles older tournaments)
    try {
      const currentPlayers = await this.deps.listTournamentPlayers(this.tournamentId);
      const hostId = this.creatorId;
      const hostMissing = hostId && !currentPlayers.some(p => p.user_id === hostId);
      if (hostMissing && this.status === 'registration') {
        const nextSeed = (currentPlayers.reduce((m, p) => Math.max(m, p.seed ?? 0), 0) + 1) || 1;
        await sb.from('tournament_players')
          .insert({ tournament_id: this.tournamentId, user_id: hostId, seed: nextSeed })
          .select('*')
          .single();
      }
    } catch {}

    // Check if tournament has started before showing view
    const isStarted = this.status === 'in_progress' || this.status === 'completed';

    if (isStarted) {
      if (this.tournamentType === 'round_robin') {
        await this.showRoundRobinLobby(t);
      } else if (t?.bracket_data) {
        // Tournament has started - show bracket view directly (hide player list to avoid flash)
        const players = await this.loadPlayers();
        this.view.show({ tournamentName: this.name, isCreator: this.isCreator, players, code: this.joinCode, hidePlayerList: true, status: this.status as any, tournamentType: this.tournamentType });
        // Tourney view is mounted: re-enable lobby auto-show guard to default behavior
        try { (window as any).suppressLobbyAutoShow = false; } catch {}
        (this.view as any).setToggleLabels?.("Bracket", (this.status === "completed" ? "Results" : "Leaderboard"));

        // If BYE auto-advances are present, apply them server-side to propagate winners
        let latestBracketData: BracketData | undefined = undefined;
        try {
          const bd = t.bracket_data as BracketData;
          if ((bd as any)?.autoAdvances && (bd as any).autoAdvances.length && this.deps.applyBracketAutoAdvances) {
            await this.deps.applyBracketAutoAdvances(this.tournamentId);
            // Refetch after applying
            const tAfter = await this.deps.getTournamentById(this.tournamentId);
            latestBracketData = tAfter?.bracket_data as BracketData | undefined;
          }
        } catch {}

        let bracket = (latestBracketData ?? (t.bracket_data as BracketData));
        this.bracketData = bracket;
        const myFocus = this.findMyMatchFocus(bracket, this.currentUserId);
        const currentRound = this.estimateCurrentRound(bracket);
        const resolver = await this.buildNameResolver(bracket);
        let avatarResolver: ((userId?: string | null) => string | undefined) | undefined;
        try {
          avatarResolver = await this.buildAvatarResolver(bracket);
          try { (window as any).__T_AV_RES = avatarResolver; } catch {}
        } catch {}
        const statuses = await this.buildLiveStatuses(bracket);
        const updated = await this.persistFinalsIfMissing(bracket, statuses);
        if (updated) {
          // Refetch latest tournament to pull server-side advancement into next round slots
          const t2 = await this.deps.getTournamentById(this.tournamentId);
          if (t2?.bracket_data) {
            bracket = t2.bracket_data as BracketData;
            this.bracketData = bracket;
          }
        }
        const displayBracket = this.applyFinalsToBracket(this.bracketData!, statuses);
        const isCompleted = (t?.status === 'completed');
        if (isCompleted) {
          // Default to results when tournament completed
          await this.showLeaderboard();
          // Also update toggle label to 'Results'
          (this.view as any).setToggleLabels?.('Bracket', 'Results');
          // After results visible, maybe show awards show overlay (once per user)
          try { await this.maybeShowAwardsShow(); } catch (e) { console.warn('Awards show display failed', e); }
        } else {
          (this.view as any).setToggleLabels?.('Bracket', 'Leaderboard');
          await this.prefetchBracketPPRs(displayBracket);
          this.view.showBracket(displayBracket, currentRound, this.currentUserId, resolver, myFocus || undefined, statuses, isCompleted, avatarResolver, this.resolveTournamentPPRSync);
          this.lobbyMode = 'bracket';
        }
      }
    } else {
      // Tournament is in registration - show player list
      const players = await this.loadPlayers();
      this.view.show({ tournamentName: this.name, isCreator: this.isCreator, players, code: this.joinCode, status: this.status as any, tournamentType: this.tournamentType });
      // Tourney view is mounted: re-enable lobby auto-show guard to default behavior
      try { (window as any).suppressLobbyAutoShow = false; } catch {}
      if (this.tournamentType === 'round_robin') {
        (this.view as any).setToggleLabels?.('Schedule', 'Standings');
      } else {
        (this.view as any).setToggleLabels?.("Bracket", (this.status === "completed" ? "Results" : "Leaderboard"));
      }
    }

    // Poll for updates every 15 seconds (fallback for realtime)
    this.startPolling();
  }

  // Display a one-time awards show overlay to the current user when tournament is completed
  private async maybeShowAwardsShow() {
    // Safety checks
    const t = await this.deps.getTournamentById(this.tournamentId);
    if (!t || t.status !== 'completed') return;
    // Fetch all players (includes stats)
    const players = await this.deps.listTournamentPlayers(this.tournamentId);
    // Find current user stats row
    const meRow = players.find(p => p.user_id === this.currentUserId);
    const seen = (meRow as any)?.stats?.hasSeenAwardsShow;
    if (seen) return; // already seen

    // Build name + avatar map
    const nameMap = new Map<string, string>();
    const avatarMap = new Map<string, string | undefined>();
    for (const p of players) {
      const u = await this.getCachedUser(p.user_id);
      nameMap.set(p.user_id, u?.user_name || p.user_id);
      avatarMap.set(p.user_id, u?.profile_image_url || undefined);
    }

    // Determine champion from bracket final or round robin standings
    let championName: string | undefined;
    try {
      if (this.tournamentType === 'round_robin') {
        const standings = Array.isArray(t.round_robin_data?.standings) ? (t.round_robin_data!.standings as any[]) : [];
        const championEntry = standings.find(e => e?.rank === 1) || standings[0];
        if (championEntry?.userId) {
          championName = nameMap.get(championEntry.userId) || undefined;
        }
      } else {
        const bd = t.bracket_data as any;
        const totalRounds = bd?.rounds?.length || 0;
        if (totalRounds > 0) {
          const final = bd.rounds[totalRounds - 1]?.matches?.[0];
          if (final && final.result && (final.a?.user_id || final.b?.user_id)) {
            const aScore = final.result.aScore ?? 0;
            const bScore = final.result.bScore ?? 0;
            const aWon = aScore >= bScore;
            const winnerId = aWon ? final.a?.user_id : final.b?.user_id;
            championName = nameMap.get(winnerId) || undefined;
          }
        }
      }
    } catch {}

        // Build award candidates (used for the overlay)
    const candidateAwards = this.buildAwardCandidates({
      players: players as any[],
      nameMap,
      includePPR: false
    });
    const distributed = this.distributeAwards(candidateAwards, players as any[], nameMap, championName);

// Group by winner name -> awards list
    const byName = new Map<string, Array<{ label: string; value?: string }>>();
    for (const a of distributed) {
      const nm = a.winner || '';
      if (!nm) continue;
      if (!byName.has(nm)) byName.set(nm, []);
      byName.get(nm)!.push({ label: a.label, value: a.value });
    }

    // Build slides for each player, ordered by roster (seed order), with champion last
    const slides = players.map(p => {
      const nm = nameMap.get(p.user_id) || p.user_id;
      const avatar = avatarMap.get(p.user_id);
      return { name: nm, avatar, awards: byName.get(nm) || [] };
    });

    // Show overlay; upon completion/skip, mark seen flag for this user
    showTournamentAwardsShow({
      tournamentName: this.name,
      slides,
      championName: championName,
      perSlideMs: 5000,
      onDone: async () => {
        try { await this.deps.upsertTournamentPlayerStats(this.tournamentId, this.currentUserId, { hasSeenAwardsShow: true }); } catch {}
      },
      onSkip: async () => {
        try { await this.deps.upsertTournamentPlayerStats(this.tournamentId, this.currentUserId, { hasSeenAwardsShow: true }); } catch {}
      }
    });
  }

  private async showPrimaryView() {
    if (this.tournamentType === 'round_robin') {
      await this.renderRoundRobinView('schedule');
      return;
    }
    await this.showCurrentBracket();
  }

  private async showSecondaryView() {
    if (this.tournamentType === 'round_robin') {
      await this.renderRoundRobinView('standings');
      return;
    }
    await this.showLeaderboard();
  }

  private async showRoundRobinLobby(tournament: any) {
    this.roundRobinData = tournament.round_robin_data ?? this.roundRobinData;
    if (!this.roundRobinData) {
      console.warn('[RoundRobin] Missing round robin data for tournament', this.tournamentId);
      return;
    }
    const players = await this.loadPlayers(true);
    this.view.show({ tournamentName: this.name, isCreator: this.isCreator, players, code: this.joinCode, hidePlayerList: true, status: this.status as any, tournamentType: this.tournamentType });
    try { (window as any).suppressLobbyAutoShow = false; } catch {}
    this.view.setToggleLabels?.('Schedule', 'Standings');
    const mode = tournament.status === 'completed' ? 'standings' : this.roundRobinViewMode;
    await this.renderRoundRobinView(mode);
  }

  private async renderRoundRobinView(mode?: 'schedule' | 'standings', updateOnly: boolean = false) {
    if (mode) this.roundRobinViewMode = mode;
    if (!this.roundRobinData) return;
    this.view.setToggleLabels?.('Schedule', 'Standings');
    const matches: RoundRobinMatch[] = Array.isArray(this.roundRobinData.schedule) ? this.roundRobinData.schedule : [];
    const totalMatches = this.roundRobinData.totalMatchCount ?? matches.filter(m => m.playerA?.userId !== BYE_USER_ID && m.playerB?.userId !== BYE_USER_ID).length;
    const rrPlayerIds = this.getRoundRobinPlayerIds(matches);
    const { resolveName, resolveAvatar, resolveTournamentPPR } = await this.buildRoundRobinResolvers(rrPlayerIds);
    const standingsData = Array.isArray(this.roundRobinData.standings) ? this.roundRobinData.standings : [];
    const playerRecords: Record<string, { wins: number; losses: number }> = {};
    standingsData.forEach((entry: any) => {
      if (!entry?.userId) return;
      playerRecords[entry.userId] = { wins: entry.wins ?? 0, losses: entry.losses ?? 0 };
    });

    const pprRows = rrPlayerIds.map(id => ({
      userId: id,
      userName: resolveName(id),
      ppr: resolveTournamentPPR(id) ?? 0,
      isMe: id === this.currentUserId
    }));

    if (this.roundRobinViewMode === 'standings') {
      const rows = standingsData.map((entry: any) => ({
        userId: entry.userId,
        userName: resolveName(entry.userId),
        wins: entry.wins ?? 0,
        losses: entry.losses ?? 0,
        pointsFor: entry.pointsFor ?? 0,
        pointsAgainst: entry.pointsAgainst ?? 0,
        pointDifferential: entry.pointDifferential ?? 0,
        matchesPlayed: entry.matchesPlayed ?? 0,
        isCurrentUser: entry.userId === this.currentUserId
      }));

      // TEMP DEBUG: Compare standings calculated from roll histories vs playerAPoints/playerBPoints
      try {
        const { standingsFromRolls, matchDebug } = await this.deps.calculateStandingsFromRollHistories(
          this.tournamentId,
          matches
        );
        console.log('[RR STANDINGS DEBUG] Match-by-match comparison (Stored vs From Rolls):');
        for (const debug of matchDebug) {
          const match = `${debug.playerA.substring(0, 8)} vs ${debug.playerB.substring(0, 8)}`;
          const aMatch = debug.playerAPointsStored === undefined ? '(no stored)' : debug.playerAPointsStored;
          const aRolls = debug.playerAPointsFromRolls;
          const bMatch = debug.playerBPointsStored === undefined ? '(no stored)' : debug.playerBPointsStored;
          const bRolls = debug.playerBPointsFromRolls;
          const aCheck = debug.playerAPointsStored === debug.playerAPointsFromRolls ? '[OK]' : '[DIFF]';
          const bCheck = debug.playerBPointsStored === debug.playerBPointsFromRolls ? '[OK]' : '[DIFF]';
          console.log(`  [${debug.matchIndex}] ${match}: A ${aMatch}->${aRolls}${aCheck} | B ${bMatch}->${bRolls}${bCheck}`);
        }
        console.log('[RR STANDINGS DEBUG] Total PF/PA comparison:');
        for (const entry of standingsData) {
          const fromRolls = standingsFromRolls.get(entry.userId);
          if (!fromRolls) continue;
          const pfMatch = entry.pointsFor === fromRolls.pointsFor ? '[OK]' : '[DIFF]';
          const paMatch = entry.pointsAgainst === fromRolls.pointsAgainst ? '[OK]' : '[DIFF]';
          console.log(`  ${resolveName(entry.userId)}: PF ${entry.pointsFor}->${fromRolls.pointsFor}${pfMatch} | PA ${entry.pointsAgainst}->${fromRolls.pointsAgainst}${paMatch}`);
        }
      } catch (err) {
        console.warn('[RR STANDINGS DEBUG] Failed to calculate from roll histories:', err);
      }

      const isCompleted = this.status === 'completed';
      let awardsData: {
        awards?: Array<{ label: string; winner?: string; value?: string }>;
        focusAwardWinnerName?: string;
        podium?: { first?: any; second?: any; third?: any };
      } | undefined;

      if (isCompleted) {
        const statsPlayers = await this.getCachedTournamentPlayers();
        const nameMap = new Map<string,string>();
        statsPlayers.forEach(p => {
          nameMap.set(p.user_id, resolveName(p.user_id));
        });
        if (this.currentUserId && !nameMap.has(this.currentUserId)) {
          nameMap.set(this.currentUserId, resolveName(this.currentUserId));
        }
        const makePodiumEntry = (entry?: any) => entry?.userId ? { name: resolveName(entry.userId), avatar: resolveAvatar(entry.userId) } : undefined;
        const podium = {
          first: makePodiumEntry(standingsData[0]),
          second: makePodiumEntry(standingsData[1]),
          third: makePodiumEntry(standingsData[2])
        };
        const candidateAwards = this.buildAwardCandidates({
          players: statsPlayers as any[],
          nameMap,
          includePPR: true,
          pprRows
        });
        const distributedAwards = this.distributeAwards(candidateAwards, statsPlayers as any[], nameMap, podium.first?.name);
        try {
          await this.persistAwardWinners(distributedAwards, nameMap);
        } catch (err) {
          console.warn('[Awards] Failed to persist RR award winners', err);
        }
        awardsData = {
          awards: distributedAwards,
          focusAwardWinnerName: this.currentUserId ? resolveName(this.currentUserId) : undefined,
          podium
        };
      }

      if (isCompleted && awardsData?.awards && awardsData.awards.length) {
        // Show carousel results for completed tournament with awards
        this.view.showRoundRobinResultsCarousel(
          awardsData.podium || {},
          rows,
          totalMatches,
          this.currentUserId,
          awardsData.awards,
          awardsData.focusAwardWinnerName
        );
      } else {
        // Show standings without carousel if not completed or no awards
        this.view.showRoundRobinStandings(rows, totalMatches, this.currentUserId, {
          awards: awardsData?.awards,
          focusAwardWinnerName: awardsData?.focusAwardWinnerName,
          podium: awardsData?.podium,
          isCompleted,
          pprRows
        });
      }
      if (!updateOnly && isCompleted) {
        try { await this.maybeShowAwardsShow(); } catch (e) { console.warn('Awards show display failed', e); }
      }
      return;
    }

    const liveStatuses = await this.buildRoundRobinLiveStatuses(matches, resolveName);

    if (updateOnly) {
      (this.view as any).updateScheduleMatches?.(matches, {
        currentUserId: this.currentUserId,
        resolveName,
        resolveAvatar,
        resolveTournamentPPR,
        liveStatuses,
        playerRecords,
        onMatchClick: (match: RoundRobinMatch) => this.handleRoundRobinMatchClick(match),
        onPlayerProfile: (userId: string, userName: string) => this.showUserProfile(userId, userName)
      });
    } else {
      this.view.showSchedule(matches, {
        currentUserId: this.currentUserId,
        resolveName,
        resolveAvatar,
        resolveTournamentPPR,
        liveStatuses,
        playerRecords,
        onMatchClick: (match: RoundRobinMatch) => this.handleRoundRobinMatchClick(match),
        onPlayerProfile: (userId: string, userName: string) => this.showUserProfile(userId, userName)
      });
    }
  }

  private async handleRoundRobinMatchClick(match: RoundRobinMatch) {
    if (!match) return;
    if (match.result) {
      await this.showTournamentMatchBoxscore({
        round: match.round,
        matchIndex: match.matchIndex,
        playerAId: match.playerA.userId,
        playerBId: match.playerB.userId
      });
      return;
    }
    if (match.playerA.userId === BYE_USER_ID || match.playerB.userId === BYE_USER_ID) return;
    const isPlayerA = match.playerA.userId === this.currentUserId;
    const isPlayerB = match.playerB.userId === this.currentUserId;
    if (!isPlayerA && !isPlayerB) {
      await this.launchObserverMode(match.round, match.matchIndex);
      return;
    }
    const opponentId = isPlayerA ? match.playerB.userId : match.playerA.userId;
    if (!opponentId) return;
    const title = this.makeRoundRobinMatchTitle(match.round, match.matchIndex);
    const matchKey = `${this.tournamentId}:${match.round}:${match.matchIndex}`;
    const matchId = uuidv5(matchKey, uuidv5.URL);
    const existing = await this.deps.getGameById(matchId);
    if (existing) {
      if (existing.status === 'active') {
        this.destroy();
        await this.deps.resumeGame(matchId);
        return;
      }
      if (existing.status === 'completed') {
        return;
      }
    }
    const mySeed = isPlayerA ? match.playerA.seed : match.playerB.seed;
    const opponentSeed = isPlayerA ? match.playerB.seed : match.playerA.seed;
    const startingPlayerId = (opponentSeed ?? 0) > (mySeed ?? 0) ? opponentId : this.currentUserId;
    await this.deps.showStartMatchModal({
      meId: this.currentUserId,
      opponentId,
      title,
      matchId,
      tournamentId: this.tournamentId,
      round: match.round,
      matchIndex: match.matchIndex,
      startingPlayerId
    });
  }

  private makeRoundRobinMatchTitle(round: number, matchIndex: number): string {
    return `Round ${round} Match ${matchIndex}`;
  }

  private async buildRoundRobinResolvers(ids: string[]): Promise<{
    resolveName: (userId?: string | null) => string;
    resolveAvatar: (userId?: string | null) => string | undefined;
    resolveTournamentPPR: (userId?: string | null) => number | undefined;
  }> {
    const uniqueIds = Array.from(new Set(ids.filter(id => !!id && id !== BYE_USER_ID)));
    const cache = new Map<string, { name: string; avatar?: string; ppr?: number }>();
    await Promise.all(uniqueIds.map(async (id) => {
      let name = id;
      let avatar: string | undefined = undefined;
      let ppr: number | undefined = undefined;
      try {
        const u = await this.getCachedUser(id);
        name = u?.user_name || id;
        avatar = u?.profile_image_url || undefined;
      } catch {
        // keep defaults
      }
      try {
        ppr = await this.getCachedPPR(id);
      } catch {
        ppr = undefined;
      }
      cache.set(id, { name, avatar, ppr });
    }));

    const resolveName = (userId?: string | null) => {
      if (!userId) return 'Player';
      if (userId === BYE_USER_ID) return 'BYE';
      return cache.get(userId)?.name || userId;
    };
    const resolveAvatar = (userId?: string | null) => {
      if (!userId || userId === BYE_USER_ID) return undefined;
      return cache.get(userId)?.avatar;
    };
    const resolveTournamentPPR = (userId?: string | null) => {
      if (!userId || userId === BYE_USER_ID) return undefined;
      return cache.get(userId)?.ppr;
    };

    return { resolveName, resolveAvatar, resolveTournamentPPR };
  }

  private getRoundRobinPlayerIds(matches: RoundRobinMatch[]): string[] {
    const ids = new Set<string>();
    for (const match of matches) {
      if (match.playerA?.userId) ids.add(match.playerA.userId);
      if (match.playerB?.userId) ids.add(match.playerB.userId);
    }
    ids.delete(BYE_USER_ID);
    return Array.from(ids);
  }

  public destroy() {
    if (this.channel) {
      try { sb.removeChannel(this.channel); } catch {}
      this.channel = null;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.view.destroy();
    // Clean up any remaining tourney-lobby nodes
    Array.from(document.querySelectorAll('#tourney-lobby')).forEach((el) => {
      try { el.parentElement?.removeChild(el); } catch {}
    });
    // Restore lobby visibility if present
    const mainLobby = document.getElementById('skeech-lobby');
    if (mainLobby) mainLobby.style.display = 'block';
    // Ensure board remains hidden until explicitly shown by renderer
    const boardRoot = document.getElementById('skeech-root');
    if (boardRoot && boardRoot.style.display !== 'none') {
      boardRoot.style.display = 'none';
    }
  }

  public async refresh() {
    const t = await this.deps.getTournamentById(this.tournamentId);
    if (!t) return;
    this.status = t.status || this.status;
    this.tournamentType = t.tournament_type || this.tournamentType;
    if (this.tournamentType === 'round_robin') {
      this.roundRobinData = t.round_robin_data || this.roundRobinData;
      await this.renderRoundRobinView();
      return;
    }
    // Apply any pending BYE auto-advances before rendering
    try {
      const bd = t.bracket_data as BracketData;
      if ((bd as any)?.autoAdvances && (bd as any).autoAdvances.length && this.deps.applyBracketAutoAdvances) {
        await this.deps.applyBracketAutoAdvances(this.tournamentId);
      }
    } catch {}
    const latest = await this.deps.getTournamentById(this.tournamentId);
    const bracket = (latest?.bracket_data || t.bracket_data) as BracketData;
    if (!bracket) return;
    this.bracketData = bracket;
    const resolver = await this.buildNameResolver(bracket);
    let avatarResolver: ((userId?: string | null) => string | undefined) | undefined;
    try {
      avatarResolver = await this.buildAvatarResolver(bracket);
      try { (window as any).__T_AV_RES = avatarResolver; } catch {}
    } catch {}
    const currentRound = this.estimateCurrentRound(bracket);
    const statuses = await this.buildLiveStatuses(bracket);
    console.log('[Tournament Refresh] Live statuses fetched:', Object.keys(statuses).length, 'matches', statuses);
    const updated = await this.persistFinalsIfMissing(bracket, statuses);
    if (updated) {
      const t2 = await this.deps.getTournamentById(this.tournamentId);
      if (t2?.bracket_data) {
        this.bracketData = t2.bracket_data as BracketData;
      }
    }
    const displayBracket = this.applyFinalsToBracket(this.bracketData!, statuses);
    const isCompleted = this.status === 'completed';
    console.log('[Tournament Refresh] Calling view.showBracket with statuses:', statuses);
    this.view.showBracket(displayBracket, currentRound, this.currentUserId, resolver, this.findMyMatchFocus(bracket, this.currentUserId) || undefined, statuses, isCompleted, avatarResolver, this.resolveTournamentPPRSync);
    this.lobbyMode = 'bracket';
  }

  private async refreshBracketDataOnly() {
    if (this.tournamentType === 'round_robin') return;
    // Update bracket data without switching views (for background polling while on leaderboard)
    const t = await this.deps.getTournamentById(this.tournamentId);
    if (!t) return;
    this.status = t.status || this.status;
    // Apply any pending BYE auto-advances
    try {
      const bd = t.bracket_data as BracketData;
      if ((bd as any)?.autoAdvances && (bd as any).autoAdvances.length && this.deps.applyBracketAutoAdvances) {
        await this.deps.applyBracketAutoAdvances(this.tournamentId);
      }
    } catch {}
    const latest = await this.deps.getTournamentById(this.tournamentId);
    const bracket = (latest?.bracket_data || t.bracket_data) as BracketData;
    if (!bracket) return;
    this.bracketData = bracket;
    // Fetch live statuses to keep data fresh
    const statuses = await this.buildLiveStatuses(bracket);
    // Persist any completed matches
    await this.persistFinalsIfMissing(bracket, statuses);
  }

  private async showCurrentBracket() {
    const t = await this.deps.getTournamentById(this.tournamentId);
    if (!t?.bracket_data) return;
    // Apply BYE auto-advances if present
    try {
      const bd = t.bracket_data as BracketData;
      if ((bd as any)?.autoAdvances && (bd as any).autoAdvances.length && this.deps.applyBracketAutoAdvances) {
        await this.deps.applyBracketAutoAdvances(this.tournamentId);
      }
    } catch {}
    const t2 = await this.deps.getTournamentById(this.tournamentId);
    const bracket = (t2?.bracket_data || t.bracket_data) as BracketData;
    const resolver = await this.buildNameResolver(bracket);
    let avatarResolver: ((userId?: string | null) => string | undefined) | undefined;
    try {
      avatarResolver = await this.buildAvatarResolver(bracket);
      try { (window as any).__T_AV_RES = avatarResolver; } catch {}
    } catch {}
    const currentRound = this.estimateCurrentRound(bracket);
    const statuses = await this.buildLiveStatuses(bracket);
    const displayBracket = this.applyFinalsToBracket(bracket, statuses);
    await this.prefetchBracketPPRs(displayBracket);
    const isCompleted = (t2?.status === 'completed') || (t?.status === 'completed');
    this.view.showBracket(displayBracket, currentRound, this.currentUserId, resolver, this.findMyMatchFocus(bracket, this.currentUserId) || undefined, statuses, isCompleted, avatarResolver, this.resolveTournamentPPRSync);
    this.lobbyMode = 'bracket';
  }

  private async showLeaderboard() {
    this.lobbyMode = 'leaderboard';
    // Show loading state immediately while we fetch leaderboard rows
    this.view.showLeaderboardLoading();
    const t = await this.deps.getTournamentById(this.tournamentId);
    const players = await this.getCachedTournamentPlayers();
    const rows: Array<{ userId?: string; userName: string; ppr: number; isMe?: boolean }> = [];
    const nameMap = new Map<string, string>();
    for (const p of players) {
      const u = await this.getCachedUser(p.user_id);
      const name = u?.user_name || p.user_id;
      nameMap.set(p.user_id, name);
      const ppr = (await this.getCachedPPR(p.user_id)) ?? 0;
      rows.push({ userId: p.user_id, userName: name, ppr, isMe: p.user_id === this.currentUserId });
    }
    // If tournament is complete and bracket is available, compute podium & awards
    const bd = t?.bracket_data;
    // When showAwardsEarly is enabled, show Awards section during the tournament (pre-completion)
    if (this.showAwardsEarly && (!t?.status || t.status !== 'completed')) {
      try {
        const awards: Array<{ label: string; winner?: string; value?: string }> = [];
        const pickMax = (key: string, fmt?: (v:any)=>string, customLabel?: string) => {
          let bestId: string | null = null; let bestVal: number | undefined = undefined;
          for (const p of players as any[]) {
            const v = Number(p?.stats?.[key] ?? 0);
            if (bestVal === undefined || v > bestVal) { bestVal = v; bestId = p.user_id; }
          }
          if (bestId !== null && bestVal !== undefined && bestVal > 0) {
            const label = customLabel || key.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase());
            awards.push({ label, winner: nameMap.get(bestId) || bestId, value: fmt ? fmt(bestVal) : String(bestVal) });
          }
        };
        const pickMinPos = (key: string, fmt?: (v:any)=>string, customLabel?: string) => {
          let bestId: string | null = null; let bestVal: number | undefined = undefined;
          for (const p of players as any[]) {
            const raw = p?.stats?.[key];
            const v = (typeof raw === 'number' && raw>0) ? raw : undefined;
            if (v === undefined) continue;
            if (bestVal === undefined || v < bestVal) { bestVal = v; bestId = p.user_id; }
          }
          if (bestId !== null && bestVal !== undefined) {
            const label = customLabel || key.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase());
            awards.push({ label, winner: nameMap.get(bestId) || bestId, value: fmt ? fmt(bestVal) : String(bestVal) });
          }
        };
        const pickPPR = () => {
          if (!rows.length) return;
          const best = rows.reduce((acc, r) => (r.ppr > (acc?.ppr ?? -Infinity) ? r : acc), undefined as any);
          if (best && typeof best.ppr === 'number' && best.userName) {
            awards.push({ label: 'Efficiency (PPR)', winner: best.userName, value: best.ppr.toFixed(2) });
          }
        };
        pickMax('skeechCount', undefined, 'Most Skrollrs');
        pickMax('busts');
        pickMax('highestTurnScore');
        pickMax('longestNoDitchStreak');
        pickMax('popUps', undefined, 'Most Pop Ups');
        pickMax('orbits', v => `${(typeof v === 'number' && !isNaN(v)) ? v.toFixed(2) : v}`, 'Longest Orbit');
        pickMax('fastestRoll', v => `${v.toFixed ? v.toFixed(2) : v} m/s`);
        pickMinPos('slowestRoll', v => `${v.toFixed ? v.toFixed(2) : v} m/s`);
        pickPPR();

        // Try to include podium if a final exists; otherwise leave blank
        const podium: { first?: any; second?: any; third?: any } = {};
        try {
          if (bd?.rounds?.length) {
            const totalRounds = bd.rounds.length;
            const finalRound = bd.rounds[totalRounds - 1];
            const semiRound = totalRounds >= 2 ? bd.rounds[totalRounds - 2] : null;
            const final = finalRound?.matches?.[0];
            if (final && final.result && (final.a?.user_id || final.b?.user_id)) {
              const aScore = final.result.aScore ?? 0;
              const bScore = final.result.bScore ?? 0;
              const aWon = aScore >= bScore;
              const winnerId = aWon ? final.a?.user_id : final.b?.user_id;
              const loserId = aWon ? final.b?.user_id : final.a?.user_id;
              const winnerUser = await this.getCachedUser(winnerId);
              const loserUser = await this.getCachedUser(loserId);
              podium.first = { name: winnerUser?.user_name || '', avatar: winnerUser?.profile_image_url || '' };
              podium.second = { name: loserUser?.user_name || '', avatar: loserUser?.profile_image_url || '' };
              if (semiRound && semiRound.matches?.length === 2) {
                const semiLosers: Array<{ uid?: string|null; score: number }> = [];
                for (const sm of semiRound.matches) {
                  if (sm?.result) {
                    const aS = sm.result.aScore ?? 0;
                    const bS = sm.result.bScore ?? 0;
                    const aWon2 = aS >= bS;
                    const loserUid = aWon2 ? sm.b?.user_id : sm.a?.user_id;
                    const loserScore = aWon2 ? bS : aS;
                    semiLosers.push({ uid: loserUid, score: loserScore });
                  }
                }
                if (semiLosers.length === 2) {
                  const best = semiLosers[0].score >= semiLosers[1].score ? semiLosers[0] : semiLosers[1];
                  if (best.uid) {
                    const thirdUser = await this.getCachedUser(best.uid);
                    podium.third = { name: thirdUser?.user_name || '', avatar: thirdUser?.profile_image_url || '' };
                  }
                }
              }
            }
          }
        } catch {}
        const dist = this.distributeAwards(awards, players as any[], nameMap, podium.first?.name);
        this.view.showLeaderboardWithPodium(podium, rows, dist, nameMap.get(this.currentUserId) || undefined, false);
        this.lobbyMode = 'leaderboard';
        (this.view as any).setToggleLabels?.('Bracket', 'Leaderboard');
        return;
      } catch {}
    }
    if (t?.status === 'completed' && bd?.rounds?.length) {
      const totalRounds = bd.rounds.length;
      const finalRound = bd.rounds[totalRounds - 1];
      const semiRound = totalRounds >= 2 ? bd.rounds[totalRounds - 2] : null;
      const final = finalRound?.matches?.[0];
      const podium: { first?: any; second?: any; third?: any } = {};
      if (final && final.result && (final.a?.user_id || final.b?.user_id)) {
        const aScore = final.result.aScore ?? 0;
        const bScore = final.result.bScore ?? 0;
        const aWon = aScore >= bScore;
        const winnerId = aWon ? final.a?.user_id : final.b?.user_id;
        const loserId = aWon ? final.b?.user_id : final.a?.user_id;
        const winnerUser = await this.getCachedUser(winnerId);
        const loserUser = await this.getCachedUser(loserId);
        podium.first = { name: winnerUser?.user_name || '', avatar: winnerUser?.profile_image_url || '' };
        podium.second = { name: loserUser?.user_name || '', avatar: loserUser?.profile_image_url || '' };
        // Determine 3rd from the two semi-final losers by higher losing score
        if (semiRound && semiRound.matches?.length === 2) {
          const semiLosers: Array<{ uid?: string|null; score: number }> = [];
          for (const sm of semiRound.matches) {
            if (sm?.result) {
              const aS = sm.result.aScore ?? 0;
              const bS = sm.result.bScore ?? 0;
              const aWon2 = aS >= bS;
              const loserUid = aWon2 ? sm.b?.user_id : sm.a?.user_id;
              const loserScore = aWon2 ? bS : aS;
              semiLosers.push({ uid: loserUid, score: loserScore });
            }
          }
          if (semiLosers.length === 2) {
            const best = semiLosers[0].score >= semiLosers[1].score ? semiLosers[0] : semiLosers[1];
            if (best.uid) {
              const thirdUser = await this.getCachedUser(best.uid);
              podium.third = { name: thirdUser?.user_name || '', avatar: thirdUser?.profile_image_url || '' };
            }
          }
        }
        // Compute awards from tournament_players.stats (best-effort)
        const awards: Array<{ label: string; winner?: string; value?: string }> = [];
        const pickMax = (key: string, fmt?: (v:any)=>string, customLabel?: string) => {
          let bestId: string | null = null; let bestVal: number | undefined = undefined;
          for (const p of players as any[]) {
            const v = Number(p?.stats?.[key] ?? 0);
            if (bestVal === undefined || v > bestVal) { bestVal = v; bestId = p.user_id; }
          }
          if (bestId !== null && bestVal !== undefined && bestVal > 0) {
            const label = customLabel || key.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase());
            awards.push({ label, winner: nameMap.get(bestId) || bestId, value: fmt ? fmt(bestVal) : String(bestVal) });
          }
        };
        const pickMinPos = (key: string, fmt?: (v:any)=>string, customLabel?: string) => {
          let bestId: string | null = null; let bestVal: number | undefined = undefined;
          for (const p of players as any[]) {
            const raw = p?.stats?.[key];
            const v = (typeof raw === 'number' && raw>0) ? raw : undefined;
            if (v === undefined) continue;
            if (bestVal === undefined || v < bestVal) { bestVal = v; bestId = p.user_id; }
          }
          if (bestId !== null && bestVal !== undefined) {
            const label = customLabel || key.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase());
            awards.push({ label, winner: nameMap.get(bestId) || bestId, value: fmt ? fmt(bestVal) : String(bestVal) });
          }
        };
        // Derived PPR: totalPoints/totalRolls with threshold
        const pickPPR = () => {
          if (!rows.length) return;
          const best = rows.reduce((acc, r) => (r.ppr > (acc?.ppr ?? -Infinity) ? r : acc), undefined as any);
          if (best && typeof best.ppr === 'number' && best.userName) {
            awards.push({ label: 'Efficiency (PPR)', winner: best.userName, value: best.ppr.toFixed(2) });
          }
        };
        pickMax('skeechCount', undefined, 'Most Skrollrs');
        pickMax('busts');
        pickMax('highestTurnScore');
        pickMax('longestNoDitchStreak');
        pickMax('popUps', undefined, 'Most Pop Ups');
        pickMax('orbits', v => `${(typeof v === 'number' && !isNaN(v)) ? v.toFixed(2) : v}`, 'Longest Orbit');
        pickMax('fastestRoll', v => `${v.toFixed ? v.toFixed(2) : v} m/s`);
        pickMinPos('slowestRoll', v => `${v.toFixed ? v.toFixed(2) : v} m/s`);
        pickPPR();

        const dist = this.distributeAwards(awards, players as any[], nameMap, podium.first?.name);
        try {
          await this.persistAwardWinners(dist, nameMap);
        } catch (err) {
          console.warn('[Awards] Failed to persist award winners', err);
        }
        this.view.showLeaderboardWithPodium(podium, rows, dist, nameMap.get(this.currentUserId) || undefined, true);
        this.lobbyMode = 'leaderboard';
        (this.view as any).setToggleLabels?.("Bracket", "Results");
        return;
      }
    }
    // Default fallback
    this.view.showLeaderboard(rows);
(this.view as any).setToggleLabels?.("Bracket", "Leaderboard");
  }

  // Ensure every participant receives at least one award. Filters negative categories and adds fillers.
  private distributeAwards(
    awards: Array<{ label: string; winner?: string; value?: string }>,
    players: Array<{ user_id: string }>,
    nameMap: Map<string,string>,
    championName?: string
  ): Array<{ label: string; winner?: string; value?: string }> {
    const out: Array<{ label: string; winner?: string; value?: string }> = [];
    const disallowed = new Set<string>(['busts','slowest roll','slowest']);
    const norm = (s:string)=> (s||'').toLowerCase();

    // Reverse name lookup
    const idByName = new Map<string,string>();
    nameMap.forEach((nm, id) => { idByName.set(nm, id); });

    // Optionally prepend Winner (Champion)
    if (championName && !awards.some(a => norm(a.label)==='winner')) {
      out.push({ label: 'Winner', winner: championName, value: '' });
    }

    // Copy allowed awards
    for (let i = 0; i < awards.length; i++) {
      const a = awards[i];
      const labelNorm = norm(a.label);
      if (Array.from(disallowed).some(b => labelNorm.includes(b))) continue;
      out.push(a);
    }

    // Track assigned userIds from winners
    const assigned = new Set<string>();
    for (let i = 0; i < out.length; i++) {
      const a = out[i];
      if (!a.winner) continue;
      const uid = idByName.get(a.winner) || '';
      if (uid) assigned.add(uid);
    }

    // Add fillers for anyone unassigned
    const fillers = ['Best Dressed','Friendliest','Most Spirited','Crowd Favorite','Fan Favorite','Style Points','Clutch Roller','Rising Star','Vibes Champion','Best Nickname'];
    let fi = 0;
    for (const p of players) {
      if (!assigned.has(p.user_id)) {
        const nm = nameMap.get(p.user_id) || p.user_id;
        const label = fillers[fi % fillers.length]; fi++;
        out.push({ label, winner: nm, value: '' });
        assigned.add(p.user_id);
      }
    }

    return out;
  }

  private async persistAwardWinners(
    awards: Array<{ label: string; winner?: string; value?: string }>,
    nameMap: Map<string, string>
  ) {
    if (!this.deps.saveTournamentAwardWinners) return;
    const idByName = new Map<string, string>();
    nameMap.forEach((nm, id) => idByName.set(nm, id));
    const winners = awards
      .map(a => {
        const uid = a.winner ? (idByName.get(a.winner) || undefined) : undefined;
        if (!uid) return null;
        return { label: a.label, userId: uid, winnerName: a.winner, value: a.value };
      })
      .filter(Boolean);
    if (!winners.length) return;
    await this.deps.saveTournamentAwardWinners(this.tournamentId, winners);
  }

  private buildAwardCandidates(params: {
    players: Array<{ user_id: string; stats?: any }>;
    nameMap: Map<string,string>;
    includePPR?: boolean;
    pprRows?: Array<{ userName: string; ppr: number; isMe?: boolean }>;
  }): Array<{ label: string; winner?: string; value?: string }> {
    const awards: Array<{ label: string; winner?: string; value?: string }> = [];
    const pickMax = (key: string, fmt?: (v:any)=>string, customLabel?: string) => {
      let bestId: string | null = null;
      let bestVal: number | undefined = undefined;
      for (const p of params.players as any[]) {
        const v = Number(p?.stats?.[key] ?? 0);
        if (bestVal === undefined || v > bestVal) { bestVal = v; bestId = p.user_id; }
      }
      if (bestId !== null && bestVal !== undefined && bestVal > 0) {
        const label = customLabel || key.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase());
        awards.push({ label, winner: params.nameMap.get(bestId) || bestId, value: fmt ? fmt(bestVal) : String(bestVal) });
      }
    };
    const pickMinPos = (key: string, fmt?: (v:any)=>string, customLabel?: string) => {
      let bestId: string | null = null;
      let bestVal: number | undefined = undefined;
      for (const p of params.players as any[]) {
        const raw = p?.stats?.[key];
        const v = (typeof raw === 'number' && raw>0) ? raw : undefined;
        if (v === undefined) continue;
        if (bestVal === undefined || v < bestVal) { bestVal = v; bestId = p.user_id; }
      }
      if (bestId !== null && bestVal !== undefined) {
        const label = customLabel || key.replace(/([A-Z])/g,' $1').replace(/^./,c=>c.toUpperCase());
        awards.push({ label, winner: params.nameMap.get(bestId) || bestId, value: fmt ? fmt(bestVal) : String(bestVal) });
      }
    };
    const pickPPR = () => {
      const rows = params.pprRows ?? [];
      if (!rows.length) return;
      const best = rows.reduce((acc, r) => (r.ppr > (acc?.ppr ?? -Infinity) ? r : acc), undefined as any);
      if (best && typeof best.ppr === 'number' && best.userName) {
        awards.push({ label: 'Efficiency (PPR)', winner: best.userName, value: best.ppr.toFixed(2) });
      }
    };
    pickMax('skeechCount', undefined, 'Most Skrollrs');
    pickMax('busts');
    pickMax('highestTurnScore');
    pickMax('longestNoDitchStreak');
    pickMax('popUps', undefined, 'Most Pop Ups');
    pickMax('orbits', (v:any) => `${(typeof v === 'number' && !isNaN(v)) ? v.toFixed(2) : v}`, 'Longest Orbit');
    pickMax('fastestRoll', (v:any) => `${v?.toFixed ? v.toFixed(2) : v} m/s`);
    pickMinPos('slowestRoll', (v:any) => `${v?.toFixed ? v.toFixed(2) : v} m/s`);
    if (params.includePPR) {
      pickPPR();
    }
    return awards;
  }

  private async loadPlayers(forceRefresh: boolean = false): Promise<TournamentPlayerVM[]> {
    const rows = await this.getCachedTournamentPlayers(forceRefresh);
    const players: TournamentPlayerVM[] = [];
    const seenUserIds = new Set<string>();

    for (const r of rows) {
      // Skip duplicates (e.g., if user joined from multiple browser tabs)
      if (seenUserIds.has(r.user_id)) continue;
      seenUserIds.add(r.user_id);

      const u = await this.getCachedUser(r.user_id);
      players.push({ userId: r.user_id, userName: u?.user_name || 'Player', profileImageUrl: u?.profile_image_url, seed: r.seed, isHost: this.creatorId === r.user_id });
    }
    // sort by seed
    players.sort((a, b) => (a.seed ?? 0) - (b.seed ?? 0));
    return players;
  }

  private async leave() {
    await this.deps.leaveTournament(this.tournamentId, this.currentUserId);
    this.destroy();
  }

  private async start() {
    if (!this.isCreator) return;
    const updated: any = this.tournamentType === 'round_robin'
      ? await this.deps.startRoundRobinTournament?.(this.tournamentId)
      : await this.deps.startTournament(this.tournamentId);
    if (!updated) return;
    this.status = 'in_progress';
    if (this.tournamentType === 'round_robin') {
      this.roundRobinData = updated.round_robin_data ?? this.roundRobinData;
      await this.showRoundRobinLobby(updated);
      return;
    }
    let bracket = (updated && (updated as any).bracket_data) as BracketData;
    try {
      if ((bracket as any)?.autoAdvances && (bracket as any).autoAdvances.length && this.deps.applyBracketAutoAdvances) {
        await this.deps.applyBracketAutoAdvances(this.tournamentId);
        const t2 = await this.deps.getTournamentById(this.tournamentId);
        if (t2?.bracket_data) bracket = t2.bracket_data as BracketData;
      }
    } catch {}
    this.bracketData = bracket;
    const currentRound = this.estimateCurrentRound(bracket);
    const resolver = await this.buildNameResolver(bracket);
    let avatarResolver: ((userId?: string | null) => string | undefined) | undefined;
    try {
      avatarResolver = await this.buildAvatarResolver(bracket);
      try { (window as any).__T_AV_RES = avatarResolver; } catch {}
    } catch {}
    await this.prefetchBracketPPRs(bracket);
    this.view.showBracket(
      bracket,
      currentRound,
      this.currentUserId,
      resolver,
      this.findMyMatchFocus(bracket, this.currentUserId) || undefined,
      undefined,
      undefined,
      avatarResolver,
      this.resolveTournamentPPRSync
    );
    this.lobbyMode = 'bracket';
  }

  private estimateCurrentRound(data: BracketData): number {
    for (const r of data.rounds) {
      // A match is considered pending only if it's a real PvP slot (not a BYE)
      // and has no recorded result yet.
      const anyPending = r.matches.some((m: any) => {
        const isBye = !!m.isBye;
        const hasBothPlayers = !!(m.a?.user_id) && !!(m.b?.user_id);
        if (isBye) return false; // BYE matches do not block round progression
        if (!hasBothPlayers) return false; // Unassigned slots shouldn't block either
        return !m.result;
      });
      if (anyPending) return r.round;
    }
    return data.rounds[data.rounds.length - 1]?.round || 1;
  }

  private findMyMatchFocus(data: BracketData, userId: string | null): { round: number; matchIndex: number } | null {
    if (!userId) return null;
    for (const r of data.rounds) {
      for (const m of r.matches) {
        if (m.a?.user_id === userId || m.b?.user_id === userId) {
          return { round: r.round, matchIndex: m.matchIndex };
        }
      }
    }
    return null;
  }

  /**
   * Cached listTournamentPlayers to avoid duplicate API calls
   */
  private async getCachedTournamentPlayers(forceRefresh: boolean = false): Promise<Array<{ user_id: string; seed: number | null; stats?: any }>> {
    const now = Date.now();
    const cacheIsFresh = this.tournamentPlayersCache && (now - this.tournamentPlayersCacheTs) < 10000; // 10s TTL
    if (this.tournamentPlayersCache && !forceRefresh && cacheIsFresh) {
      return this.tournamentPlayersCache;
    }
    const players = await this.deps.listTournamentPlayers(this.tournamentId);
    this.tournamentPlayersCache = players as any[];
    this.tournamentPlayersCacheTs = now;
    return this.tournamentPlayersCache;
  }

  /**
   * Cached getUserById to avoid duplicate API calls
   */
  private async getCachedUser(userId: string): Promise<{ user_name?: string; profile_image_url?: string } | null> {
    if (this.userCache.has(userId)) {
      return this.userCache.get(userId)!;
    }
    try {
      const user = await this.deps.getUserById(userId);
      const cached = { user_name: user?.user_name, profile_image_url: user?.profile_image_url };
      this.userCache.set(userId, cached);
      return cached;
    } catch {
      return null;
    }
  }

  /**
   * Cached calculateUserPPRForTournament to avoid duplicate calls within same render
   * Cache expires after 10 seconds to allow fresh data on next poll
   */
  private async getCachedPPR(userId: string): Promise<number | undefined> {
    const now = Date.now();
    const cacheAge = now - this.pprCacheTimestamp;
    if (cacheAge > 10000) {
      // Cache expired, clear it
      this.pprCache.clear();
      this.pprCacheTimestamp = now;
    }
    if (this.pprCache.has(userId)) {
      return this.pprCache.get(userId);
    }
    try {
      const ppr = await this.deps.calculateUserPPRForTournament(this.tournamentId, userId);
      this.pprCache.set(userId, ppr);
      return ppr;
    } catch {
      return undefined;
    }
  }

  private resolveTournamentPPRSync = (userId?: string | null) => {
    if (!userId) return undefined;
    return this.pprCache.get(userId);
  };

  private async prefetchBracketPPRs(bracket: BracketData) {
    const ids = new Set<string>();
    bracket.rounds.forEach(r => r.matches.forEach(m => {
      if (m.a?.user_id) ids.add(m.a.user_id);
      if (m.b?.user_id) ids.add(m.b.user_id);
    }));
    await Promise.all(Array.from(ids).map(id => this.getCachedPPR(id)));
  }

  private async resolvePlayerName(userId?: string | null): Promise<string> {
    if (!userId) return 'Player';
    if (this.playerNameCache.has(userId)) return this.playerNameCache.get(userId)!;
    try {
      const user = await this.getCachedUser(userId);
      const name = user?.user_name || 'Player';
      this.playerNameCache.set(userId, name);
      return name;
    } catch {
      return 'Player';
    }
  }

  private async showTournamentMatchBoxscore(params: { round: number; matchIndex: number; playerAId?: string | null; playerBId?: string | null }) {
    const { round, matchIndex, playerAId, playerBId } = params;
    let [p1Name, p2Name] = await Promise.all([
      this.resolvePlayerName(playerAId),
      this.resolvePlayerName(playerBId)
    ]);

    let gameRow: any = null;
    if (playerAId && playerBId && this.deps.findTournamentGame) {
      try {
        gameRow = await this.deps.findTournamentGame(playerAId, playerBId, this.tournamentId, round, matchIndex);
      } catch (e) {
        console.warn('[Boxscore] Failed to load tournament game via findTournamentGame', e);
      }
    }

    if (!gameRow && this.deps.getGameById) {
      try {
        const deterministicId = uuidv5(`${this.tournamentId}:${round}:${matchIndex}`, uuidv5.URL);
        gameRow = await this.deps.getGameById(deterministicId);
      } catch (e) {
        console.warn('[Boxscore] Failed to load deterministic tournament game row', e);
      }
    }

    if (!gameRow && playerAId && playerBId && this.deps.findLatestGameBetweenUsers) {
      try {
        gameRow = await this.deps.findLatestGameBetweenUsers(playerAId, playerBId);
      } catch (e) {
        console.warn('[Boxscore] Failed to load latest game between users', e);
      }
    }

    const subtitle = `Round ${round} - Match ${matchIndex}`;
    const p1Rolls = Array.isArray(gameRow?.p1_roll_history) ? gameRow.p1_roll_history : [];
    const p2Rolls = Array.isArray(gameRow?.p2_roll_history) ? gameRow.p2_roll_history : [];
    const hasHistory = p1Rolls.length > 0 || p2Rolls.length > 0;

    if (gameRow?.p1_user_id) {
      p1Name = await this.resolvePlayerName(gameRow.p1_user_id);
    }
    if (gameRow?.p2_user_id) {
      p2Name = await this.resolvePlayerName(gameRow.p2_user_id);
    }

    showBoxscoreModal({
      title: 'Tournament Boxscore',
      subtitle,
      contextNote: hasHistory ? undefined : 'No roll history found for this match.',
      p1Name,
      p2Name,
      p1Rolls,
      p2Rolls
    });
  }

  private async showUserProfile(userId?: string | null, userName?: string) {
    if (!userId || userId === BYE_USER_ID) return;
    const resolvedName = userName || await this.resolvePlayerName(userId);
    try {
      this.deps.showUserProfile?.(userId, resolvedName);
    } catch (err) {
      console.warn('[Tournament] Failed to show user profile', err);
    }
  }

  private async handleMatchClick(info: { round: number; matchIndex: number; aUserId?: string | null; bUserId?: string | null; result?: { aScore?: number; bScore?: number } | null; shouldObserve?: boolean }) {
    const a = info.aUserId || null;
    const b = info.bUserId || null;

    // Observer mode: user is not a player, match is in progress (no result yet)
    if (info.shouldObserve) {
      await this.launchObserverMode(info.round, info.matchIndex);
      return;
    }

    // Ended? (has result)
    if (info.result) {
      await this.showTournamentMatchBoxscore({
        round: info.round,
        matchIndex: info.matchIndex,
        playerAId: info.aUserId,
        playerBId: info.bUserId
      });
      return;
    }
    // Not started or in-progress
    if (this.currentUserId && (this.currentUserId === a || this.currentUserId === b)) {
      // Current user is in this unstarted match: show ready-to-start modal
      const opponentId = this.currentUserId === a ? (b as string) : (a as string);
      if (opponentId) {
        const title = this.makeMatchTitle(info.round);
        const deterministicKey = `${this.tournamentId}:${info.round}:${info.matchIndex}`;
        // Deterministic, valid UUID for this tournament match
        const matchId = uuidv5(deterministicKey, uuidv5.URL);
        // If a game already exists for this match, handle accordingly
        const existing = await this.deps.getGameById(matchId);
        if (existing && existing.status === 'active') {
          this.destroy();
          await this.deps.resumeGame(matchId);
          return;
        }
        if (existing && existing.status === 'completed') {
          // Completed games should not be re-started; simply ignore click for now
          return;
        }

        // Determine starting player based on tournament seeding
        // Higher seed (e.g., 7) goes first; lower seed (e.g., 2) is "home team" and goes second
        let startingPlayerId: string | undefined = undefined;
        try {
          const players = await this.deps.listTournamentPlayers(this.tournamentId);
          const currentSeed = players.find(p => p.user_id === this.currentUserId)?.seed || 999;
          const opponentSeed = players.find(p => p.user_id === opponentId)?.seed || 999;
          // Higher seed number goes first
          startingPlayerId = opponentSeed > currentSeed ? opponentId : this.currentUserId;
        } catch (e) {
          console.warn('Failed to determine starting player from seeds', e);
        }

        await this.deps.showStartMatchModal({ meId: this.currentUserId, opponentId, title, matchId, tournamentId: this.tournamentId, round: info.round, matchIndex: info.matchIndex, startingPlayerId });
      }
      return;
    }
    // Not my match and not started: do nothing for now
  }

  /**
   * Launch observer mode for a tournament match
   */
  private async launchObserverMode(round: number, matchIndex: number) {
    console.log(`[Tournament] Launching observer mode for Round ${round}, Match ${matchIndex}`);

    try {
      // Get match game details from database
      const matchGame = this.deps.getTournamentMatchGame ? await this.deps.getTournamentMatchGame(this.tournamentId, round, matchIndex) : null;

      if (!matchGame) {
        console.warn('[Tournament] No active game found for this match - cannot observe');
        // TODO: Show user-friendly message
        return;
      }

      const { matchId, player1, player2 } = matchGame;

      // Hide tournament UI (just hide the DOM element temporarily)
      const tourneyEl = document.getElementById('tourney-lobby');
      if (tourneyEl) tourneyEl.style.display = 'none';

      // Launch observer mode via game instance
      await this.deps.initializeAsObserver?.(matchId, player1, player2, { tournamentId: this.tournamentId, round, matchIndex });

      console.log(`[Tournament] Observer mode launched for match ${matchId}`);
    } catch (error) {
      console.error('[Tournament] Failed to launch observer mode:', error);
      // TODO: Show user-friendly error message
    }
  }

  private makeMatchTitle(roundNumber: number): string {
    const bd = this.bracketData;
    if (!bd) return `Round ${roundNumber} Matchup`;
    const totalRounds = bd.rounds.length;
    const round = bd.rounds.find(r => r.round === roundNumber);
    const matches = round?.matches.length || 0;
    if (matches === 1 && roundNumber === totalRounds) return 'Championship Matchup';
    if (matches === 2 && roundNumber === totalRounds - 1) return 'Semi-final Matchup';
    return `Round ${roundNumber} Matchup`;
  }

  private async buildNameResolver(data: BracketData) {
    const ids = new Set<string>();
    for (const r of data.rounds) {
      for (const m of r.matches) {
        if (m.a?.user_id) ids.add(m.a.user_id);
        if (m.b?.user_id) ids.add(m.b.user_id);
      }
    }
    const cache = new Map<string, string>();
    await Promise.all(Array.from(ids).map(async (id) => {
      try {
        const u = await this.getCachedUser(id);
        cache.set(id, u?.user_name || id);
      } catch {
        cache.set(id, id);
      }
    }));
    return (userId?: string | null) => {
      if (!userId) return '—';
      return cache.get(userId) || userId;
    };
  }

  private async buildAvatarResolver(data: BracketData) {
    const ids = new Set<string>();
    for (const r of data.rounds) {
      for (const m of r.matches) {
        if (m.a?.user_id) ids.add(m.a.user_id);
        if (m.b?.user_id) ids.add(m.b.user_id);
      }
    }
    const cache = new Map<string, string | undefined>();
    await Promise.all(Array.from(ids).map(async (id) => {
      try {
        const u = await this.getCachedUser(id);
        cache.set(id, u?.profile_image_url || undefined);
      } catch {
        cache.set(id, undefined);
      }
    }));
    return (userId?: string | null) => {
      if (!userId) return undefined;
      return cache.get(userId);
    };
  }

  private async buildLiveStatuses(data: BracketData): Promise<Record<string, import('./BracketView').MatchLiveStatus | undefined>> {
    const statuses: Record<string, import('./BracketView').MatchLiveStatus | undefined> = {};
    const tasks: Array<Promise<void>> = [];
    for (const r of data.rounds) {
      for (const m of r.matches) {
        if (m.result) continue; // Completed
        // Only if both players assigned
        if (!m.a?.user_id || !m.b?.user_id) continue;
        const matchId = uuidv5(`${this.tournamentId}:${r.round}:${m.matchIndex}`, uuidv5.URL);
        tasks.push((async () => {
          try {
            let game = await this.deps.getGameById(matchId);
            console.log(`[buildLiveStatuses] Round ${r.round} Match ${m.matchIndex} - matchId: ${matchId}, game found:`, !!game, game?.status, 'has game_state_json:', !!game?.game_state_json);
            if (!game) {
              // Fallback: find game by players and tournament context
              const aId = m.a?.user_id || '';
              const bId = m.b?.user_id || '';
              if (aId && bId) {
                game = await this.deps.findTournamentGame(aId, bId, this.tournamentId, r.round, m.matchIndex);
              }
            }
            if (game && game.status === 'active' && game.game_state_json) {
              const gs = game.game_state_json || {};
              // Map p1/p2 scores to bracket A/B positions by user id
              let aScore = 0, bScore = 0;
              if (game.p1_user_id === (m.a!.user_id)) {
                aScore = Array.isArray(gs.scores) ? (gs.scores[0] ?? 0) : (game.p1_score ?? 0);
                bScore = Array.isArray(gs.scores) ? (gs.scores[1] ?? 0) : (game.p2_score ?? 0);
              } else {
                // Swap if p1 is actually bracket B
                aScore = Array.isArray(gs.scores) ? (gs.scores[1] ?? 0) : (game.p2_score ?? 0);
                bScore = Array.isArray(gs.scores) ? (gs.scores[0] ?? 0) : (game.p1_score ?? 0);
              }
              const currentPlayerIndex = gs.currentPlayer ?? 0;
              const currentPlayerId = currentPlayerIndex === 0 ? game.p1_user_id : game.p2_user_id;
              const currentPlayerSide: 'A' | 'B' = (currentPlayerId === (m.a!.user_id)) ? 'A' : 'B';
              const name = await this.getCachedUser(currentPlayerId);
              const currentPlayerName = name?.user_name || 'Player';
              statuses[`${r.round}:${m.matchIndex}`] = {
                aScore,
                bScore,
                round: gs.round ?? 1,
                ballsLeft: gs.ballsLeft ?? 0,
                currentPlayerName,
                currentPlayerSide,
                phase: gs.phase,
                suddenDeathRound: gs.suddenDeathRound,
              };
            } else if (game && game.status === 'completed') {
              // Completed game: check if it ended in sudden death or regular play
              let aScore = 0, bScore = 0;
              let isSuddenDeathFinal = false;
              const gs = game.game_state_json || {};

              // If phase is 'over' and we were in sudden death, use sudden death scores
              if (gs.phase === 'over' && Array.isArray(gs.suddenDeathScores) && (gs.suddenDeathScores[0] > 0 || gs.suddenDeathScores[1] > 0)) {
                // Map sudden death scores to bracket A/B using the p1_user_id mapping
                if (game.p1_user_id === (m.a!.user_id)) {
                  aScore = Number(gs.suddenDeathScores[0] ?? 0);
                  bScore = Number(gs.suddenDeathScores[1] ?? 0);
                } else {
                  aScore = Number(gs.suddenDeathScores[1] ?? 0);
                  bScore = Number(gs.suddenDeathScores[0] ?? 0);
                }
                isSuddenDeathFinal = true;
              } else if (Array.isArray(gs.scores)) {
                // Fall back to regular scores from game_state_json if present
                if (game.p1_user_id === (m.a!.user_id)) {
                  aScore = Number(gs.scores[0] ?? 0);
                  bScore = Number(gs.scores[1] ?? 0);
                } else {
                  aScore = Number(gs.scores[1] ?? 0);
                  bScore = Number(gs.scores[0] ?? 0);
                }
              } else {
                // Use regular game scores from p1_score/p2_score columns
                if (game.p1_user_id === (m.a!.user_id)) {
                  aScore = game.p1_score ?? 0;
                  bScore = game.p2_score ?? 0;
                } else {
                  aScore = game.p2_score ?? 0;
                  bScore = game.p1_score ?? 0;
                }
              }
              statuses[`${r.round}:${m.matchIndex}`] = {
                aScore,
                bScore,
                round: 0,
                ballsLeft: 0,
                currentPlayerName: '',
                isFinal: true,
                phase: isSuddenDeathFinal ? 'sudden_death' : undefined,
                suddenDeathRound: gs.suddenDeathRound,
              } as any;
            } else {
              // leave blank if truly no record found
            }
          } catch {
            // Ignore failures for individual matches
          }
        })());
      }
    }
    await Promise.all(tasks);
    return statuses;
  }

  private async buildRoundRobinLiveStatuses(matches: RoundRobinMatch[], resolveName?: NameResolver): Promise<Record<string, MatchLiveStatus | undefined>> {
    const statuses: Record<string, MatchLiveStatus | undefined> = {};
    const tasks: Array<Promise<void>> = [];
    for (const match of matches) {
      const includeCompleted = Boolean(match.result);
      const aId = match.playerA?.userId;
      const bId = match.playerB?.userId;
      if (!aId || !bId) continue;
      if (aId === BYE_USER_ID || bId === BYE_USER_ID) continue;
      tasks.push((async () => {
        const status = await this.loadLiveStatusForMatch(match.round, match.matchIndex, aId, bId, includeCompleted, resolveName);
        if (status) {
          statuses[`${match.round}:${match.matchIndex}`] = status;
        }
      })());
    }
    await Promise.all(tasks);
    return statuses;
  }

  private async loadLiveStatusForMatch(
    round: number,
    matchIndex: number,
    playerAId: string,
    playerBId: string,
    includeCompleted: boolean,
    resolveName?: NameResolver
  ): Promise<MatchLiveStatus | undefined> {
    const matchId = uuidv5(`${this.tournamentId}:${round}:${matchIndex}`, uuidv5.URL);
    try {
      let game = await this.deps.getGameById(matchId);
      if (!game) {
        game = await this.deps.findTournamentGame(playerAId, playerBId, this.tournamentId, round, matchIndex);
      }
      if (!game) return undefined;

      const gs = game.game_state_json || {};
      const mapScoresFromArray = (arr: any[]): { aScore: number; bScore: number } => {
        const first = Number(arr?.[0] ?? 0);
        const second = Number(arr?.[1] ?? 0);
        const isPlayerAP1 = game.p1_user_id === playerAId;
        return isPlayerAP1 ? { aScore: first, bScore: second } : { aScore: second, bScore: first };
      };
      const mapScoresFromColumns = (): { aScore: number; bScore: number } => {
        const p1 = Number(game.p1_score ?? 0);
        const p2 = Number(game.p2_score ?? 0);
        const isPlayerAP1 = game.p1_user_id === playerAId;
        return isPlayerAP1 ? { aScore: p1, bScore: p2 } : { aScore: p2, bScore: p1 };
      };

      if (game.status === 'active' && game.game_state_json) {
        const useSuddenDeathScores = gs.phase === 'sudden_death' && Array.isArray(gs.suddenDeathScores);
        const { aScore, bScore } = useSuddenDeathScores
          ? mapScoresFromArray(gs.suddenDeathScores)
          : (Array.isArray(gs.scores) ? mapScoresFromArray(gs.scores) : mapScoresFromColumns());
        const currentPlayerIndex = gs.currentPlayer ?? 0;
        let currentPlayerId = currentPlayerIndex === 0 ? game.p1_user_id : game.p2_user_id;
        currentPlayerId = currentPlayerId || playerAId;
        const currentPlayerName = resolveName ? resolveName(currentPlayerId) : (await this.getCachedUser(currentPlayerId))?.user_name || 'Player';
        const currentPlayerSide: 'A' | 'B' = currentPlayerId === playerAId ? 'A' : 'B';
        return {
          aScore,
          bScore,
          round: gs.round ?? 1,
          ballsLeft: gs.ballsLeft ?? 0,
          currentPlayerName,
          currentPlayerSide,
          phase: gs.phase,
          suddenDeathRound: gs.suddenDeathRound
        };
      }

      if (includeCompleted && game.status === 'completed') {
        let aScore = 0, bScore = 0;
        let isSuddenDeathFinal = false;
        if (Array.isArray(gs.suddenDeathScores) && gs.phase === 'over' && (gs.suddenDeathScores[0] > 0 || gs.suddenDeathScores[1] > 0)) {
          const sudden = mapScoresFromArray(gs.suddenDeathScores);
          aScore = sudden.aScore;
          bScore = sudden.bScore;
          isSuddenDeathFinal = true;
        } else if (Array.isArray(gs.scores)) {
          const mapped = mapScoresFromArray(gs.scores);
          aScore = mapped.aScore;
          bScore = mapped.bScore;
        } else {
          const mapped = mapScoresFromColumns();
          aScore = mapped.aScore;
          bScore = mapped.bScore;
        }
        return {
          aScore,
          bScore,
          round: 0,
          ballsLeft: 0,
          currentPlayerName: '',
          isFinal: true,
          phase: isSuddenDeathFinal ? 'sudden_death' : undefined,
          suddenDeathRound: gs.suddenDeathRound
        };
      }
    } catch {
      // ignore failures for individual matches
    }
    return undefined;
  }

  private applyFinalsToBracket(bracket: BracketData, statuses: Record<string, import('./BracketView').MatchLiveStatus | undefined>): BracketData {
    // Clone shallowly for safe render updates
    const clone: BracketData = { ...bracket, rounds: bracket.rounds.map(r => ({ ...r, matches: r.matches.map(m => ({ ...m })) })) } as any;
    for (const r of clone.rounds) {
      for (const m of r.matches) {
        const key = `${r.round}:${m.matchIndex}`;
        const st = statuses[key];
        if (!m.result && st?.isFinal) {
          m.result = { aScore: st.aScore, bScore: st.bScore } as any;
        }
      }
    }
    return clone;
  }

  private async persistFinalsIfMissing(bracket: BracketData, statuses: Record<string, import('./BracketView').MatchLiveStatus | undefined>): Promise<boolean> {
    // For any match with no bracket result but with a discovered final in DB, record it so the next render has updated bracket_data
    const tasks: Array<Promise<any>> = [];
    for (const r of bracket.rounds) {
      for (const m of r.matches) {
        if (m.result) continue;
        const st = statuses[`${r.round}:${m.matchIndex}`];
        if (st && st.isFinal && m.a?.user_id && m.b?.user_id) {
          const aScore = st.aScore;
          const bScore = st.bScore;
          const winnerUserId = aScore >= bScore ? m.a.user_id : m.b.user_id;
          tasks.push(this.deps.recordTournamentMatchResult({
            tournamentId: this.tournamentId,
            round: r.round,
            matchIndex: m.matchIndex,
            aScore,
            bScore,
            winnerUserId
          }));
        }
      }
    }
    if (!tasks.length) return false;
    try {
      await Promise.all(tasks);
      return true;
    } catch {
      return false;
    }
  }

  private startPolling() {
    // Don't poll immediately - we just loaded all data in open()
    // Start polling after the first interval to avoid duplicate fetches
    this.pollInterval = setInterval(() => this.pollForUpdates(), TIMING.TOURNAMENT_POLL_INTERVAL);
  }

  private async pollForUpdates() {
    try {
      // Check for tournament status changes (registration -> in_progress)
      const t = await this.deps.getTournamentById(this.tournamentId);
      if (!t) return;
      this.tournamentType = t.tournament_type || this.tournamentType;
      if (this.tournamentType === 'round_robin') {
        this.roundRobinData = t.round_robin_data || this.roundRobinData;
      }

      const newStatus = t.status || 'registration';

      // If tournament started, navigate to bracket
      if (newStatus === 'in_progress' && this.status === 'registration') {
        this.status = newStatus;
        if (this.tournamentType === 'round_robin') {
          await this.showRoundRobinLobby(t);
          return;
        }
        if (t.bracket_data) {
          const bracket = t.bracket_data as BracketData;
          this.bracketData = bracket;
          const currentRound = this.estimateCurrentRound(bracket);
          const resolver = await this.buildNameResolver(bracket);
          let avatarResolver: ((userId?: string | null) => string | undefined) | undefined;
          try {
            avatarResolver = await this.buildAvatarResolver(bracket);
            try { (window as any).__T_AV_RES = avatarResolver; } catch {}
          } catch {}
          const myFocus = this.findMyMatchFocus(bracket, this.currentUserId);
          const statuses = await this.buildLiveStatuses(bracket);
          const displayBracket = this.applyFinalsToBracket(bracket, statuses);
          await this.prefetchBracketPPRs(displayBracket);
          this.view.showBracket(
            displayBracket,
            currentRound,
            this.currentUserId,
            resolver,
            myFocus || undefined,
            statuses,
            undefined,
            avatarResolver,
            this.resolveTournamentPPRSync
          );
          this.lobbyMode = 'bracket';
        }
        return; // Bracket view shown, continue polling for live updates
      }

      // If tournament is completed, stop polling
      if (newStatus === 'completed') {
        this.status = newStatus;
        if (this.pollInterval) {
          console.log('[Tournament Poll] Tournament completed, stopping polling');
          clearInterval(this.pollInterval);
          this.pollInterval = null;
        }
        if (this.tournamentType === 'round_robin') {
          await this.renderRoundRobinView('standings');
        }
        return;
      }

      // If tournament is in progress, continuously update bracket with live game data
      if (newStatus === 'in_progress') {
        this.status = newStatus;
        // Check if tournament lobby view is visible (regardless of bracket/leaderboard toggle)
        const tourneyLobby = document.getElementById('tourney-lobby');
        if (this.tournamentType === 'round_robin') {
          if (tourneyLobby && tourneyLobby.style.display !== 'none') {
            console.log('[Tournament Poll] Refreshing round-robin data...');
            // Check if schedule view is currently visible - if so, use incremental update to preserve carousel position
            const scheduleVisible = tourneyLobby.querySelector('.tourney-schedule-viewport');
            await this.renderRoundRobinView(undefined, !!scheduleVisible);
          }
          return;
        }
          if (tourneyLobby && tourneyLobby.style.display !== 'none') {
            if (this.lobbyMode === 'leaderboard') {
              console.log('[Tournament Poll] Leaderboard active, refreshing bracket data in background...');
              await this.refreshBracketDataOnly();
            } else {
              console.log('[Tournament Poll] Refreshing bracket with live data...');
              await this.refresh();
            }
          }
        return;
      }

      // If still in registration, update player list
      if (this.status === 'registration') {
        const updated = await this.loadPlayers(true);
        this.view.updatePlayers(updated);
      }
    } catch (err) {
      console.error('Tournament poll error:', err);
    }
  }

  public async manualRefresh() {
    await this.pollForUpdates();
  }
}









